import { useEffect, useRef, useState } from "react";
import { api } from "../api.js";
import { clearSession, getAccessToken, hasStoredUser } from "../auth.js";
import { isAccessTokenStale } from "./jwtExp.js";

// Gates an authenticated layout mount until we know we hold a fresh
// access token. Two distinct entry conditions:
//
//   1. Fresh page load — the access token only ever lives in memory, so
//      it is null. If a stored user profile exists in localStorage we
//      assume the user wants to be signed in and try /v1/auth/refresh
//      (the browser auto-attaches the HttpOnly eb_refresh cookie).
//   2. Returning tab — we already have an access token. If it is more
//      than 60s from expiry, render immediately; otherwise refresh
//      pre-emptively to avoid an authed-call → 401 → retry storm.
//
// Behaviour:
//   • No stored user *and* no access token → "unauthed" (layout
//     redirects to /login without firing any API call).
//   • Access token fresh (> 60s of life) → "ready" immediately.
//   • Otherwise await api.refreshSession() (itself single-flighted
//     inside api.ts). Success → "ready"; failure → clearSession() +
//     "unauthed".
//   • Visibility-change re-check so a tab that slept through the
//     refresh window refreshes on focus rather than on first click.
//
// The hook never throws; a network error on refresh collapses to
// "unauthed" just like a 401.

export type BootstrapPhase = "bootstrapping" | "ready" | "unauthed";

const FRESHNESS_BUFFER_SECONDS = 60;

export function useAuthBootstrap(): BootstrapPhase {
  const [phase, setPhase] = useState<BootstrapPhase>(() => {
    const token = getAccessToken();
    if (!token) {
      // No in-memory token. If we don't even have a stored profile,
      // skip the network call entirely.
      return hasStoredUser() ? "bootstrapping" : "unauthed";
    }
    if (!isAccessTokenStale(token, FRESHNESS_BUFFER_SECONDS)) return "ready";
    return "bootstrapping";
  });
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    let mounted = true;

    async function ensureFresh(): Promise<void> {
      const token = getAccessToken();
      if (token && !isAccessTokenStale(token, FRESHNESS_BUFFER_SECONDS)) {
        if (mounted && phase !== "ready") setPhase("ready");
        return;
      }
      // Either no token in memory, or it is close to expiry. Try a refresh;
      // the underlying fetch is single-flighted in api.ts so concurrent
      // callers share one network round-trip. The HttpOnly cookie is
      // attached automatically.
      try {
        const ok = await api.refreshSession();
        if (!mounted) return;
        if (ok) {
          setPhase("ready");
        } else {
          clearSession();
          setPhase("unauthed");
        }
      } catch {
        if (!mounted) return;
        clearSession();
        setPhase("unauthed");
      }
    }

    void ensureFresh();

    // Re-run on tab re-focus. useSessionExpiry has its own
    // visibilitychange handler for the warn-toast path, but that
    // hook can't gate rendering — this one can.
    function onVisibility() {
      if (document.visibilityState === "visible") void ensureFresh();
    }
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      mounted = false;
      cancelledRef.current = true;
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // `phase` is intentionally excluded: the effect should run once
    // on mount and thereafter only in response to visibility
    // changes. Including it would cause an infinite re-run loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return phase;
}
